/**
 * Page: CreateVideoTemplate
 * Admin-only page to create a new video template. Handles auth gate,
 * submission to the API, and basic UX states.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { ArrowLeft } from 'lucide-react';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import VideoTemplateForm from '../../../components/admin/videoTemplates/VideoTemplateForm';
import Footer from '../../../components/shared/Footer';
import { createVideoTemplate } from '../../../api/content/videoTemplatesApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function CreateVideoTemplate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submitError, setSubmitError] = useState('');

  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await createVideoTemplate(formData);
      
      if (response && response.videoTemplateId) {
        addToast('Your video template has been created successfully!', 'success');
        navigate('/app/private/manageVideoTemplates');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('[CreateVideoTemplate] Error creating video template:', error);
      
      let errorMessage = 'Failed to create video template. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
      addToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading create video template..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/templates')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Templates
        </button>
      </div>

      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create Video Template</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Upload and create a new video template for the library.
          </p>
        </div>
        <button
          onClick={() => navigate('/app/private/manageVideoTemplates')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
        >
          Manage Video Templates
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm p-6">
        {submitError && (
          <div className="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error Creating Video Template</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <VideoTemplateForm 
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submitButtonText="Create Video Template"
        />
      </div>

      <Footer />
    </div>
  );
}
